// components/SupplierInvite/SupplierInviteContent.tsx
import React from "react";
import { Button } from "../Form";
import { Color } from "../Form/Button";
import { SupplierInviteActions, SupplierInviteState } from "./userSupplierState";
import { Box } from "@mui/material";

interface SupplierInviteContentProps {
  state: SupplierInviteState;
  actions: SupplierInviteActions;
  translate: (key: string) => string;
  classes: any;
}

export const SupplierInviteContent = ({
  state,
  actions,
  translate,
  classes,
}: SupplierInviteContentProps) => {
  const getTitle = (): string => {
    if (state.inviteDeclined) {
      return translate("inviteDeclined");
    }

    if (state.isInvalidToken) {
      return `${translate("sorryTheLinkIsBroken")} :(`;
    }

    if (state.isExpiredToken) {
      return translate("sorryTheLinkHasExpired");
    }

    return translate("companyInvitation");
  };

  const renderDescription = (): JSX.Element => {
    const dataCy = "description";

    if (state.isInvalidToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkIsBroken")}
        </p>
      );
    }

    if (state.isExpiredToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkHasExpiredYouCanRequestANewOneByClickingOnTheButtonBelow")}
        </p>
      );
    }

    if (state.inviteDeclined) {
      return (
        <>
          <p className={classes.invitationDescription} data-cy={dataCy}>
            {translate("weWillNotifyTheAdministrator")}
          </p>
          <p className={classes.invitationDescription}>{translate("youCanCloseThisWindow")}</p>
        </>
      );
    }

    return (
      <p className={classes.invitationDescription} data-cy={dataCy}>
        {translate("youHaveBeenInvitedToBePartOf")}
        {"   "}
        <span>{translate("supplierPortal")}</span>{" "}
        {translate("youCanAcceptOrDeclineTheInvitationAndWeWillNotifyTheCompanyAdministrator")}
      </p>
    );
  };

  const renderActions = (): JSX.Element => {
    if (state.isExpiredToken) {
      return (
        <Button
          onClick={actions.handleResendInvite}
          loading={state.isLoadingInviteSupplier}
          disabled={state.isLoadingInviteSupplier}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="resend-button"
        >
          {translate("resendInviteLink")}
        </Button>
      );
    }

    if (state.isInvalidToken) {
      return (
        <Button
          onClick={actions.goBackToLogin}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="go-back-button"
        >
          {translate("goBackToLoginPage")}
        </Button>
      );
    }

    if (state.inviteDeclined) {
      return (
        <p className={classes.invitationDescriptionDeclined} data-cy="description">
          {translate("ifYouRejectedThisInvitationByMistakePleaseRequestANewInvite")}{" "}
          <a href="#" onClick={actions.handleInviteUserAgain} data-cy="invite-user-again">
            {`${translate("here")}.`}
          </a>
        </p>
      );
    }

    return (
      <Box display="flex" gap="24px" justifyContent="center" alignItems="center" width="100%" paddingTop="32px">
        <Button
          onClick={actions.handleRejectInvite}
          fullWidth
          loading={state.isLoadingInviteSupplier}
          disabled={state.isLoadingInviteSupplier}
          customColor={Color.ERROR}
          className={classes.actionButton}
          data-cy="decline-button"
        >
          {translate("decline")}
        </Button>

        <Button
          onClick={actions.handleAcceptInvite}
          fullWidth
          disabled={state.isLoadingInviteSupplier}
          loading={state.isLoadingInviteSupplier}
          customColor={Color.SUCCESS}
          className={classes.actionButton}
          data-cy="accept-button"
        >
          {translate("accept")}
        </Button>
      </Box>
    );
  };

  return {
    title: getTitle(),
    description: renderDescription(),
    actions: renderActions(),
  };
};
